import {
  addDays,
  daysBetween,
  describeDaysLeft,
  formatIsoDate,
  formatQuantity,
  guessQuantity,
  type IsoDate,
  isIsoDate,
  type NewItem,
  parseQuantity,
  QUICK_EXPIRY,
  type Quantity,
  STORAGE_ICON,
  type StoragePlace,
  UNIT_DIMENSION,
} from "@larder/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { CaptureSheet, type Caught } from "../camera/CaptureSheet";
import { CountField } from "../components/CountField";
import { QuantityField } from "../components/QuantityField";

/**
 * Manual entry — the floor that always works.
 *
 * Deliberately built BEFORE the camera. It is the fastest path for a
 * repurchase, the one that works when the light is bad or a scan fails, and it
 * makes the check-in and calendar testable without pointing a phone at a
 * packet. See docs/product/intake.md and the V1 spine plan.
 *
 * The target is name + one tap. A relative chip beats a date picker because
 * nobody knows the exact date, but everyone knows "about a week" — and "no
 * date" is a first-class answer, not a skip, because salt and shampoo are real
 * things people want listed.
 *
 * Several packs of one thing are several ITEMS, not one item with a count —
 * see shared/model/batch.ts for why. This screen hides that: you say how many
 * came home, pick one date, and only if you want to are you asked for a date
 * per pack. Either way the save lands as one batch.
 */
export function AddItem({
  today,
  places,
  onAdd,
  onAddPlace,
  onCancel,
  busy,
  error,
  initial,
  canScan,
  timezone,
}: {
  today: IsoDate;
  places: StoragePlace[];
  /** Create a place without leaving this screen. Returns the new id. */
  onAddPlace: (name: string) => Promise<string | null>;
  /** One entry per pack. Several when the user said several came home. */
  onAdd: (items: NewItem[]) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
  /** Pre-filled by a scan. ALWAYS editable — see the note below. */
  initial?: NewItem | null;
  canScan: boolean;
  timezone: string;
}) {
  const nameField = useRef<HTMLInputElement>(null);
  /** The camera fills in one field on this screen: the name. See ADR 0017. */
  const [capturingName, setCapturingName] = useState(false);
  const [name, setName] = useState(initial?.name ?? "");
  // Read off a label, never typed, so they have no field of their own — but
  // dropping them on save would throw away half of what the camera found.
  const [brand, setBrand] = useState<string | null>(initial?.brand ?? null);
  const [size, setSize] = useState<string | null>(initial?.sizeText ?? null);
  /**
   * How much of it there is.
   *
   * Seeded from a scanned label, because "1 L" printed on the pack is evidence
   * and needs no confirming. Otherwise left NULL — enrichment fills it in after
   * the save, so a blank here costs the user nothing.
   */
  const [qty, setQty] = useState<Quantity | null>(
    initial?.size ?? parseQuantity(initial?.sizeText) ?? null,
  );
  // Default to the first place the household defined, not to a global guess.
  const [storageId, setStorageId] = useState<string | null>(
    initial?.storageId ?? places[0]?.id ?? null,
  );
  const [expiry, setExpiry] = useState<IsoDate | null>(initial?.expiryDate ?? null);
  // "No date" is the starting state AND a valid choice, so the chip only
  // highlights once it has actually been chosen. Otherwise the form looks like
  // it has already decided for you.
  const [datePicked, setDatePicked] = useState(!!initial?.expiryDate);
  const [showPicker, setShowPicker] = useState(false);
  /** How many packs came home. Each becomes its own item. */
  const [count, setCount] = useState(1);
  /**
   * A date per pack, only once the user has asked for that.
   *
   * Null means "the one date above goes on every pack" — the common case, and
   * the default, so adding three identical boxes costs no more taps than
   * adding one. Once opened, each row starts from the shared date so you only
   * change the ones that differ.
   */
  const [perPack, setPerPack] = useState<(IsoDate | null)[] | null>(null);
  const [addingPlace, setAddingPlace] = useState(false);
  const [newPlace, setNewPlace] = useState("");
  const [savingPlace, setSavingPlace] = useState(false);
  const placeField = useRef<HTMLInputElement>(null);
  const scanned = !!initial;

  // Focus the new-place field the moment it appears, so it behaves like the
  // one-tap chips beside it rather than adding a step.
  useEffect(() => {
    if (addingPlace) placeField.current?.focus();
  }, [addingPlace]);

  async function savePlace() {
    const name = newPlace.trim();
    if (!name || savingPlace) return;
    setSavingPlace(true);
    try {
      const id = await onAddPlace(name);
      // Selecting it is the whole point — you named it because that is where
      // this item goes.
      if (id) setStorageId(id);
      setNewPlace("");
      setAddingPlace(false);
    } finally {
      setSavingPlace(false);
    }
  }

  // Focused via a ref rather than `autoFocus`: the attribute is an a11y
  // hazard on pages generally, and this is the one screen where the keyboard
  // should already be up — it exists only to type a name.
  // A scan already filled the name, so stealing focus would only pop the
  // keyboard over the values the user is here to CHECK.
  useEffect(() => {
    if (!scanned) nameField.current?.focus();
  }, [scanned]);

  const trimmed = name.replace(/\s+/g, " ").trim();
  const canSave = trimmed.length > 0 && !busy;
  const daysLeft = expiry ? daysBetween(today, expiry) : null;
  // The per-pack list tracks the count: a pack added after the list was
  // opened inherits the shared date, a pack removed takes its row with it.
  const packDates: (IsoDate | null)[] | null = perPack
    ? Array.from(
        { length: count },
        (_, i) => perPack[i] ?? (i < perPack.length ? null : expiry),
      )
    : null;
  // Recomputed as they type, so the suggestion appears the moment the name is
  // recognisable. Free and local — no call, no wait.
  // "1 pack?" is no suggestion on a screen that already counts packs.
  const suggested = useMemo(() => {
    const g = guessQuantity(trimmed);
    return g?.unit === "pack" ? null : g;
  }, [trimmed]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    const place = places.find((p) => p.id === storageId);
    const dates = packDates ?? Array.from({ length: count }, () => expiry);
    onAdd(
      dates.map((date) => ({
        name: trimmed,
        storageId: place?.id ?? null,
        // The KIND is what shelf-life advice keys off, so it is stored on the
        // item too — renaming or deleting a place must not change how long
        // something is expected to last.
        storage: place?.kind ?? "pantry",
        brand,
        sizeText: size,
        size: qty,
        expiryDate: date,
        // Every date on this screen is now chosen by hand — a chip, "today", or
        // the picker — so it is day-precise and it is yours. Nothing here reads
        // a label, so there is no label precision to preserve and no
        // manufacture date to carry. See ADR 0017.
        expiryPrecision: date ? "day" : "none",
        // One date typed once and copied onto every pack is a different claim
        // from a date set on this pack — the record says which.
        expirySource: !date
          ? "none"
          : packDates || count === 1
            ? "user_edited"
            : "group_applied",
        mfgDate: null,
      })),
    );
  }

  if (capturingName) {
    return (
      <CaptureSheet
        timezone={timezone}
        onClose={() => setCapturingName(false)}
        onCaught={(found: Caught) => {
          if (found.productName) {
            setName(found.productName);
            if (found.brand) setBrand(found.brand);
            if (found.sizeText) {
              setSize(found.sizeText);
              // A net quantity printed on the pack is the best answer there is —
              // better than a guess and better than enrichment's typical size.
              const read = parseQuantity(found.sizeText);
              if (read) setQty(read);
            }
          }
          setCapturingName(false);
        }}
      />
    );
  }

  return (
    <section aria-label="add an item">
      {/* Same shape as the item screen: the way out sits ON the title's row
          rather than hanging in a band above it. */}
      <header className="detail-head">
        <button
          type="button"
          className="back"
          data-testid="back-btn"
          onClick={onCancel}
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="detail-ident">
          <h2>{scanned ? "Check this before saving" : "Add something"}</h2>
        </div>
      </header>
      {scanned && (
        <p className="tagline">
          Read off the label. Extraction is the part of this app most likely to be
          confidently wrong, so nothing is saved until you have looked at it.
        </p>
      )}

      <form onSubmit={submit}>
        <label className="field">
          <span className="label">What is it?</span>
          {/* The camera sits INSIDE the field rather than being its own route
              in. Scanning is a faster way to fill this box, not a different
              way to add a thing — so it never leaves the form, and whatever
              comes back is editable the moment it lands. */}
          <div className="field-with-cam">
            <input
              ref={nameField}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Milk, coriander, atta…"
              autoComplete="off"
              enterKeyHint="done"
              data-testid="item-name"
            />
            {canScan && (
              <button
                type="button"
                className="fieldcam"
                onClick={() => setCapturingName(true)}
                aria-label="Scan the name off the pack"
                data-testid="scan-name"
              >
                📷
              </button>
            )}
          </div>
        </label>

        {/* Sits right under the name, because it is a fact about the same
            thing — and above the date, because "how much" is answered by
            looking at the packet while "how long" needs a decision.

            One row, read as a sentence: "2 packs of 1 L". The count and the
            size are two facts (shared/model/item.ts on `qty` vs `size`) but
            shown as two sections they looked like two controls for one idea —
            "2 pack" beside "2 packs" made no sense at all. */}
        <fieldset className="group">
          <legend className="label">How much?</legend>
          <div className="qty-row">
            {/* Two groups that each survive a line break on a phone: "2
                packs" on one line and "of 1 L" on the next still reads. */}
            <span className="qty-part">
              <CountField value={count} onChange={setCount} idPrefix="add-count" />
              <span className="note qty-join" data-testid="count-summary">
                {count === 1 ? "pack" : "packs"}
              </span>
            </span>
            <span className="qty-part">
              {qty && (
                <span className="note qty-join" aria-hidden="true">
                  {UNIT_DIMENSION[qty.unit] === "count" ? "×" : "of"}
                </span>
              )}
              <QuantityField
                value={qty}
                suggestion={suggested}
                onChange={setQty}
                idPrefix="add-qty"
                addLabel="+ Size"
                hideUnits={["pack"]}
              />
            </span>
          </div>
        </fieldset>

        <fieldset className="group">
          <legend className="label">Good until</legend>
          {/* No camera here on purpose. A printed date is the slowest, least
              reliable thing to photograph and the chips below beat it on
              nearly every pack — see ADR 0017. */}
          <div className="chips">
            {QUICK_EXPIRY.map((q) => {
              const iso = addDays(today, q.days);
              return (
                <button
                  key={q.label}
                  type="button"
                  className={`chip ${expiry === iso ? "on" : ""}`}
                  data-testid={`expiry-${q.days}`}
                  onClick={() => {
                    setExpiry(expiry === iso ? null : iso);
                    setDatePicked(true);
                    setShowPicker(false);
                  }}
                >
                  {q.label}
                </button>
              );
            })}
            <button
              type="button"
              className={`chip ${datePicked && expiry === null ? "on" : ""}`}
              data-testid="expiry-none"
              onClick={() => {
                setExpiry(null);
                setDatePicked(true);
                setShowPicker(false);
              }}
            >
              Doesn't go off
            </button>
            <button
              type="button"
              className={`chip ${expiry === today ? "on" : ""}`}
              data-testid="expiry-today"
              onClick={() => {
                setExpiry(expiry === today ? null : today);
                setDatePicked(true);
                setShowPicker(false);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className={`chip ${showPicker ? "on" : ""}`}
              onClick={() => setShowPicker((v) => !v)}
              data-testid="expiry-pick"
            >
              Pick a date
            </button>
          </div>

          {showPicker && (
            <input
              type="date"
              className="date-input"
              value={expiry ?? ""}
              min={addDays(today, -365)}
              onChange={(e) => {
                setExpiry(isIsoDate(e.target.value) ? e.target.value : null);
              }}
              data-testid="expiry-date"
            />
          )}

          {/* Only when there IS a date. "Doesn't go off" needs no explaining —
              the chip is already selected and says so, and a paragraph
              reassuring you about salt was answering a question nobody asked. */}
          {expiry && daysLeft !== null && (
            <p className="note" data-testid="expiry-summary">
              {daysLeft < 0
                ? // Adding something already past its date is a real thing people
                  // do — a stocktake, or a jar found at the back. Say what will
                  // happen rather than quietly filing it under waste.
                  `${formatIsoDate(expiry)} — ${describeDaysLeft(daysLeft)}. We'll ask you what to do with it.`
                : `${formatIsoDate(expiry)} — ${describeDaysLeft(daysLeft)}`}
            </p>
          )}

          {/* Several packs, one date chosen: say what is about to happen and
              offer the other way. A nudge, not a demand — a multipack from one
              shop almost always shares a date, and "apply to all" is the
              answer most of the time. Only once a date is picked, because
              until then there is nothing to apply. */}
          {count > 1 && datePicked && !packDates && (
            <div className="inline-add">
              <span className="note" data-testid="packs-same-date">
                {expiry
                  ? `Same date for all ${count} packs.`
                  : `None of the ${count} packs go off.`}
              </span>
              <button
                type="button"
                className="chip dashed"
                onClick={() => setPerPack(Array.from({ length: count }, () => expiry))}
                data-testid="packs-split"
              >
                Different dates?
              </button>
            </div>
          )}

          {packDates && (
            <div className="pack-dates" data-testid="pack-dates">
              {packDates.map((d, i) => (
                <label className="pack-date" key={`pack-${String(i)}`}>
                  <span className="note">Pack {i + 1}</span>
                  <input
                    type="date"
                    className="date-input"
                    value={d ?? ""}
                    min={addDays(today, -365)}
                    onChange={(e) => {
                      const next = [...packDates];
                      next[i] = isIsoDate(e.target.value) ? e.target.value : null;
                      setPerPack(next);
                    }}
                    data-testid={`pack-date-${i + 1}`}
                  />
                </label>
              ))}
              <button
                type="button"
                className="ghost"
                onClick={() => setPerPack(null)}
                data-testid="packs-same"
              >
                Same date for all
              </button>
            </div>
          )}
        </fieldset>

        <fieldset className="group">
          <legend className="label">Where does it go?</legend>
          <div className="chips">
            {places.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`chip ${storageId === p.id ? "on" : ""}`}
                data-testid={`storage-${p.name}`}
                onClick={() => setStorageId(p.id)}
              >
                {/* Decoration, not information — the name is right beside it,
                    so a screen reader gains nothing by announcing the glyph. */}
                <span className="chip-icon" aria-hidden="true">
                  {STORAGE_ICON[p.kind]}
                </span>
                {p.name}
              </button>
            ))}

            {/* Adding a place mid-flow, rather than sending someone to settings
                and losing what they had typed. You realise the cupboard needs a
                name at exactly the moment you are putting something in it. */}
            {!addingPlace && (
              <button
                type="button"
                className="chip dashed"
                onClick={() => setAddingPlace(true)}
                data-testid="new-place-chip"
              >
                + New spot
              </button>
            )}
          </div>

          {addingPlace && (
            <div className="inline-add">
              <input
                ref={placeField}
                value={newPlace}
                onChange={(e) => setNewPlace(e.target.value)}
                placeholder="Cupboard (green)"
                autoComplete="off"
                enterKeyHint="done"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void savePlace();
                  }
                  if (e.key === "Escape") setAddingPlace(false);
                }}
                data-testid="new-place-name"
              />
              <button
                type="button"
                className="chip"
                disabled={!newPlace.trim() || savingPlace}
                onClick={savePlace}
                data-testid="save-new-place"
              >
                {savingPlace ? "…" : "Add"}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setAddingPlace(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </fieldset>

        {error && (
          <p className="hint" data-testid="add-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="primary"
          disabled={!canSave}
          data-testid="save-item"
        >
          {busy
            ? "Saving…"
            : scanned
              ? "Looks right — save"
              : count > 1
                ? `Add ${count} packs`
                : "Add it"}
        </button>
      </form>
    </section>
  );
}
